// Roles screen: list roles for the active org (system + custom), create/edit/delete
// custom roles, and open the permissions drawer. System roles are read-only.

import { useState } from "react"
import { adminApi } from "../api/endpoints.js"
import type { RoleDto } from "../api/types.js"
import { useWritesEnabled } from "../auth/AdminAuthContext.js"
import { RoleFormDrawer } from "../components/RoleFormDrawer.js"
import { RolePermissionsDrawer } from "../components/RolePermissionsDrawer.js"
import { fr } from "../i18n/fr.js"
import { messageForError } from "../lib/errors.js"
import { useAsync } from "../lib/useAsync.js"
import { useOrg } from "../state/org.js"
import { ConfirmDialog } from "../ui/ConfirmDialog.js"
import { KeyIcon, PencilIcon, TrashIcon } from "../ui/icons.js"
import { Alert, Button, Chip, IconButton, Spinner } from "../ui/primitives.js"
import { useNotify } from "../ui/toast.js"

type FormState = { kind: "closed" } | { kind: "create" } | { kind: "edit"; role: RoleDto }

export function RolesPage() {
  const notify = useNotify()
  const writesEnabled = useWritesEnabled()
  const { activeOrg } = useOrg()

  const roles = useAsync<RoleDto[]>(
    () => (activeOrg ? adminApi.listRoles(activeOrg) : Promise.resolve([])),
    [activeOrg]
  )

  const [form, setForm] = useState<FormState>({ kind: "closed" })
  const [permsRoleId, setPermsRoleId] = useState<string | null>(null)
  const [toDelete, setToDelete] = useState<RoleDto | null>(null)
  const [deleting, setDeleting] = useState(false)

  async function confirmDelete() {
    if (!toDelete) return
    setDeleting(true)
    try {
      await adminApi.deleteRole(toDelete.id)
      notify.success(fr.roles.remove.button)
      setToDelete(null)
      roles.reload()
    } catch (err) {
      notify.error(messageForError(err, fr.roles.create.errorGeneric))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{fr.roles.title}</h1>
          <p>{fr.roles.description}</p>
        </div>
        <Button
          variant="primary"
          disabled={!writesEnabled || !activeOrg}
          onClick={() => setForm({ kind: "create" })}
        >
          {fr.roles.createButton}
        </Button>
      </div>

      {roles.loading ? <Spinner /> : null}
      {roles.error ? <Alert variant="error">{fr.roles.error}</Alert> : null}

      {roles.data && !roles.loading ? (
        roles.data.length === 0 ? (
          <Alert>{fr.roles.empty}</Alert>
        ) : (
          <div className="table-wrap">
            <table className="table table--roles">
              <thead>
                <tr>
                  <th>{fr.roles.columns.displayName}</th>
                  <th>{fr.roles.columns.key}</th>
                  <th className="table__num">{fr.roles.columns.permissionCount}</th>
                  <th>{fr.roles.columns.type}</th>
                  <th className="table__actions">{fr.roles.columns.actions}</th>
                </tr>
              </thead>
              <tbody>
                {roles.data.map((role) => (
                  <tr key={role.id}>
                    <td>
                      <div>{role.displayName || role.name}</div>
                      {role.description ? <div className="subtitle">{role.description}</div> : null}
                    </td>
                    <td className="mono">{role.name}</td>
                    <td className="table__num">{role.permissionCount}</td>
                    <td>
                      {role.isSystem ? (
                        <Chip system>{fr.roles.systemBadge}</Chip>
                      ) : (
                        <Chip>{fr.roles.customBadge}</Chip>
                      )}
                    </td>
                    {/*
                      Icon actions. The three text controls used to wrap onto two
                      lines in this column; icons keep the row one line tall. Each
                      IconButton takes the exact French word it replaces as its
                      accessible name (aria-label) AND its hover tooltip (title),
                      so the meaning is never carried by the glyph alone.
                      The disabled logic is unchanged: system roles stay
                      non-editable and everything locks when writes are dark.
                    */}
                    <td className="table__actions">
                      <div className="table__actions-row">
                        <IconButton
                          label={fr.roles.permissions.button}
                          onClick={() => setPermsRoleId(role.id)}
                        >
                          <KeyIcon />
                        </IconButton>
                        <IconButton
                          label={fr.roles.edit.button}
                          disabled={!writesEnabled || role.isSystem}
                          onClick={() => setForm({ kind: "edit", role })}
                        >
                          <PencilIcon />
                        </IconButton>
                        <IconButton
                          label={fr.roles.remove.button}
                          variant="danger"
                          disabled={!writesEnabled || role.isSystem}
                          onClick={() => setToDelete(role)}
                        >
                          <TrashIcon />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {form.kind !== "closed" && activeOrg ? (
        <RoleFormDrawer
          mode={form.kind}
          org={activeOrg}
          role={form.kind === "edit" ? form.role : undefined}
          onClose={() => setForm({ kind: "closed" })}
          onSaved={() => roles.reload()}
        />
      ) : null}

      {permsRoleId ? (
        <RolePermissionsDrawer
          roleId={permsRoleId}
          onClose={() => setPermsRoleId(null)}
          onSaved={() => roles.reload()}
        />
      ) : null}

      <ConfirmDialog
        open={toDelete !== null}
        title={fr.roles.remove.dialogTitle}
        message={fr.roles.remove.dialogMessage}
        confirmText={fr.roles.remove.confirm}
        cancelText={fr.app.cancel}
        submitting={deleting}
        onConfirm={confirmDelete}
        onClose={() => setToDelete(null)}
      />
    </div>
  )
}
